import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import ka from './ka.json';
import en from './en.json';

const translations = { ka, en };
const SUPPORTED_LANGUAGES = ['ka', 'en'];
const DEFAULT_LANGUAGE = 'ka'; // Georgian as default
const STORAGE_KEY = 'lockerhq_language';

const I18nContext = createContext(null);

/**
 * Get a nested value from an object using dot notation
 * e.g., get(obj, 'auth.login') => obj.auth.login
 */
function getNestedValue(obj, path) {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : null;
  }, obj);
}

/**
 * I18nProvider - Wrap your app with this to enable translations
 * 
 * Usage:
 *   <I18nProvider>
 *     <App />
 *   </I18nProvider>
 */
export function I18nProvider({ children, defaultLanguage }) {
  const [language, setLanguageState] = useState(() => {
    // Try to load from localStorage
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && SUPPORTED_LANGUAGES.includes(saved)) {
        return saved;
      }
    } catch (e) {
      // localStorage not available
    }
    return defaultLanguage || DEFAULT_LANGUAGE;
  });

  // Persist language choice
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, language);
    } catch (e) {
      // localStorage not available
    }
    // Set document direction and lang attribute
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((lang) => {
    if (SUPPORTED_LANGUAGES.includes(lang)) {
      setLanguageState(lang);
    }
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguageState(prev => prev === 'ka' ? 'en' : 'ka');
  }, []);

  /**
   * Translate a key with optional interpolation
   * 
   * Usage:
   *   t('auth.login')                    => "შესვლა"
   *   t('email.pickupGreeting')           => "გამარჯობა"
   *   t('parcel.boxSize')                 => "ბოქსის ზომა"
   *   t('custom.key', { name: 'John' })   => replaces {{name}} with 'John'
   */
  const t = useCallback((key, params = {}) => {
    const currentTranslations = translations[language] || translations[DEFAULT_LANGUAGE];
    let value = getNestedValue(currentTranslations, key);

    // Fallback to English if key not found in current language
    if (value === null && language !== 'en') {
      value = getNestedValue(translations.en, key);
    }

    // Return key itself if no translation found
    if (value === null) {
      console.warn(`[i18n] Missing translation: ${key}`);
      return key;
    }

    // Interpolate params: "Hello {{name}}" + {name: "John"} => "Hello John"
    if (typeof value === 'string' && Object.keys(params).length > 0) {
      return Object.entries(params).reduce((str, [paramKey, paramValue]) => {
        return str.replace(new RegExp(`{{${paramKey}}}`, 'g'), paramValue);
      }, value);
    }

    return value;
  }, [language]);

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,
    isGeorgian: language === 'ka',
    isEnglish: language === 'en',
    supportedLanguages: SUPPORTED_LANGUAGES,
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

/**
 * useTranslation hook - use in any component
 * 
 * Usage:
 *   const { t, language, toggleLanguage } = useTranslation();
 *   return <h1>{t('auth.loginTitle')}</h1>;
 */
export function useTranslation() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useTranslation must be used within an I18nProvider');
  }
  return context;
}

/**
 * LanguageSwitcher component - drop-in language toggle button
 * 
 * Usage:
 *   <LanguageSwitcher />                    // default style
 *   <LanguageSwitcher className="my-class" /> // custom class
 */
export function LanguageSwitcher({ className = '', style = {} }) {
  const { language, toggleLanguage } = useTranslation();

  return (
    <button
      onClick={toggleLanguage}
      className={`language-switcher ${className}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        border: '1px solid #ddd',
        borderRadius: '6px',
        background: '#fff',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 500,
        transition: 'all 0.2s',
        ...style,
      }}
      title={language === 'ka' ? 'Switch to English' : 'ქართულზე გადართვა'}
    >
      <span style={{ fontSize: '16px' }}>
        {language === 'ka' ? '🇬🇧' : '🇬🇪'}
      </span>
      <span>
        {language === 'ka' ? 'EN' : 'ქარ'}
      </span>
    </button>
  );
}

/**
 * LanguageSelect component - dropdown language selector
 */
export function LanguageSelect({ className = '' }) {
  const { language, setLanguage, t } = useTranslation();

  return (
    <select
      value={language}
      onChange={(e) => setLanguage(e.target.value)}
      className={`language-select ${className}`}
      style={{
        padding: '6px 10px',
        border: '1px solid #ddd',
        borderRadius: '6px',
        fontSize: '14px',
        cursor: 'pointer',
      }}
    >
      <option value="ka">{t('common.georgian')}</option>
      <option value="en">{t('common.english')}</option>
    </select>
  );
}

export default { I18nProvider, useTranslation, LanguageSwitcher, LanguageSelect };
